import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import MyGroupsIcon from '../../../MyComponents/icons/MyGroupsIcon';
import LibrarySideMenuIcon from '../../../MyComponents/icons/LibrarySideMenuIcon';
import { LIBRARY_LINK, MANAGE_CLASSES_LINK, tutorialLinks } from '../../../common/urlLinks';
import { CARD, KICKER, TEXT_LINK, cx } from './dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';

const useStyles = makeStyles(() => ({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: '18px',
    },
    tile: {
        ...CARD,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '18px',
        padding: '22px 24px',
    },
    disc: {
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        width: '48px',
        height: '48px',
        borderRadius: '16px',
        background: REDESIGN_COLORS.mist,
    },
    icon: {
        width: '22px',
        height: '22px',
    },
    body: {
        minWidth: 0,
    },
    kicker: KICKER,
    count: {
        margin: '4px 0 6px',
        fontSize: '1.35rem',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    pending: {
        color: REDESIGN_COLORS.muted,
    },
    text: {
        margin: '0 0 12px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
        lineHeight: 1.5,
    },
    link: TEXT_LINK,
}));

/**
 * The two numbers a teacher checks first: how many groups they are in and how
 * many boards their library holds. Each tile links on to the page that manages
 * it; an empty library points at the tutorial chapter instead.
 */
const DashboardStats = ({ groupsCount, boardsCount, loading }) => {
    const classes = useStyles();

    const tiles = [
        {
            key: 'groups',
            icon: <MyGroupsIcon className={classes.icon} />,
            count: groupsCount,
            pending: loading,
            to: MANAGE_CLASSES_LINK,
            linkId: 'redesign.home.dashboard.groups.link',
        },
        {
            key: 'library',
            icon: <LibrarySideMenuIcon className={classes.icon} />,
            count: boardsCount || 0,
            pending: boardsCount === null,
            to: boardsCount > 0 ? LIBRARY_LINK : tutorialLinks.library,
            linkId:
                boardsCount > 0
                    ? 'redesign.home.dashboard.library.link'
                    : 'redesign.home.dashboard.library.linkEmpty',
        },
    ];

    return (
        <LandingSection background="none" gutter={0} paddingTop={0} paddingBottom={40}>
            <div className={classes.grid}>
                {tiles.map(({ key, icon, count, pending, to, linkId }) => (
                    <div key={key} className={classes.tile}>
                        <span aria-hidden="true" className={classes.disc}>
                            {icon}
                        </span>
                        <div className={classes.body}>
                            <span className={classes.kicker}>
                                <FormattedMessage id={`redesign.home.dashboard.${key}.label`} />
                            </span>
                            <p className={cx(classes.count, pending && classes.pending)}>
                                {pending ? (
                                    '…'
                                ) : (
                                    <FormattedMessage id={`redesign.home.dashboard.${key}.count`} values={{ count }} />
                                )}
                            </p>
                            <p className={classes.text}>
                                <FormattedMessage id={`redesign.home.dashboard.${key}.text`} />
                            </p>
                            <Link to={to} className={classes.link}>
                                <FormattedMessage id={linkId} />
                                <span aria-hidden="true">→</span>
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        </LandingSection>
    );
};

export default DashboardStats;
